import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { AccountShell } from "@/components/customer/AccountShell";
import { AddressForm } from "@/components/customer/AddressForm";
import { Button } from "@/components/ui/button";
import { useCustomerAddresses } from "@/hooks/useCustomerAccount";
import type { CustomerAddress } from "@/lib/customer/customer-api";

export const Route = createFileRoute("/_customer/account/addresses")({
  component: AddressesPage,
});

function formatAddress(a: CustomerAddress): string {
  return [a.address_line1, a.address_line2, a.landmark, a.delivery_area_name, a.city, a.pincode]
    .filter(Boolean)
    .join(", ");
}

function AddressesPage() {
  const { addresses, isLoading, create, update, archive, makeDefault } = useCustomerAddresses();
  const [editing, setEditing] = useState<CustomerAddress | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <AccountShell title="Saved addresses" description="Save the places you order to most often.">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading addresses…</p>
      ) : (
        <div className="space-y-4">
          {addresses.length === 0 && !adding ? (
            <p className="rounded-xl border border-dashed border-border/70 p-6 text-sm text-muted-foreground">
              You haven't saved any addresses yet.
            </p>
          ) : null}

          {addresses.map((address) =>
            editing?.id === address.id ? (
              <AddressForm
                key={address.id}
                initial={address}
                busy={update.isPending}
                onCancel={() => setEditing(null)}
                onSubmit={async (input) => {
                  try {
                    await update.mutateAsync({ id: address.id, input });
                    setEditing(null);
                    toast.success("Address updated");
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "Could not save address");
                  }
                }}
              />
            ) : (
              <article key={address.id} className="rounded-xl border border-border/60 bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold">
                      {address.label || "Delivery address"}
                      {address.is_default ? (
                        <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-foreground">
                          Default
                        </span>
                      ) : null}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{formatAddress(address)}</p>
                    {address.contact_name || address.contact_phone ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {[address.contact_name, address.contact_phone].filter(Boolean).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(address)}>
                    Edit
                  </Button>
                  {!address.is_default ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={makeDefault.isPending}
                      onClick={async () => {
                        try {
                          await makeDefault.mutateAsync(address.id);
                          toast.success("Default address updated");
                        } catch (err) {
                          toast.error(
                            err instanceof Error ? err.message : "Could not set default address",
                          );
                        }
                      }}
                    >
                      Make default
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    disabled={archive.isPending}
                    onClick={async () => {
                      try {
                        await archive.mutateAsync(address.id);
                        toast.success("Address removed");
                      } catch (err) {
                        toast.error(
                          err instanceof Error ? err.message : "Could not remove address",
                        );
                      }
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </article>
            ),
          )}

          {adding ? (
            <AddressForm
              busy={create.isPending}
              onCancel={() => setAdding(false)}
              onSubmit={async (input) => {
                try {
                  await create.mutateAsync(input);
                  setAdding(false);
                  toast.success("Address saved");
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Could not save address");
                }
              }}
            />
          ) : (
            <Button onClick={() => setAdding(true)}>Add a new address</Button>
          )}
        </div>
      )}
    </AccountShell>
  );
}
